
<template>
  <div class="row columns account-history-info">
    <table class="unstriped">
      <thead>
          <tr>
            <th scope="col" class="text-left">{{ $translate('TABLE_HEADING_PRODUCTS') }}</th>
            <th scope="col" class="quantity text-center">{{ $translate('TABLE_HEADING_QUANTITY') }}</th>
            <th scope="col" class="total text-right">{{ $translate('ACCOUNT_ORDER_PRODUCT_TOTAL') }}</th>
          </tr>
      </thead>
      <tbody>
        <tr v-for="row in orderRows.Rows" :key="row.RowNo">
          <td class="info">
            <div class="image">
              <div v-if="row.imageUrl">
                <a v-if="row.href" :href="row.href">
                  <div v-if="row.imageLoading" class="spinner"></div>
                  <img :src="row.imageUrl" :alt="row.Name" style="max-width: 100px;">
                </a>
              </div>
              <div v-else>
                <span class="no-image">
                  <CameraIcon  />
                </span>
              </div>
            </div>
            <div class="data">
              <a v-if="row.href" :href="row.href" class="name" >{{ row.Name }}</a>
              <p v-else class="name">{{ row.Name }}</p>
              <p class="article-number">{{ row.Article_Number }}</p>
            </div>
          </td>
          <td class="text-center quantity">
            <span class="quantity-num">{{ formatQuantity(row.OrderedQuantity) }}</span>
          </td>
          <td class="total text-right">
            {{ formatOrderValue(row.RowValue) }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="summary-row">
      <div class="summary-row__left-column">
        <p>{{ $translate('ACCOUNT_ORDER_DATE') }}: {{ props.orderData.OrderDateFormatted }}</p>
        <p>{{ $translate('C_ACCOUNT_ORDER_CUSTOMER_REF') }}: {{ props.orderData.CustomerRef }}</p>
        <p>{{ $translate('C_ACCOUNT_ORDER_MARKING') }}: {{ props.orderData.Marking }}</p>
        <p>{{ $translate('ACCOUNT_ORDER_STATUS') }}: {{ props.orderData.Status }}</p>
      </div>
      <div class="summary-row__right-column">
        <p class="price">{{ $translate('ACCOUNT_ORDER_PRODUCT_TOTAL') }}:</p>
        <p class="price">{{ formatOrderValue(props.orderData.OrderValue) }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, inject, computed } from 'vue';
import CameraIcon from '@/components/icons/CameraIcon.vue';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';

const { $translate } = inject('i18n');

const props = defineProps({
  orderNo: {
    required: true,
    type: String
  },
  orderData: {
    type: Object,
    required: false
  },
  currency: {
    type: String,
    required: false,
    default: 'SEK'
  }
});

// Pull API
const API_URL = '/ajax.php?mode=external_fetchdata';
const orderRows = ref({ Rows: [] });

onMounted(async() => {
  const rows = await getOrderRows();

  // For each row, get image
  for (const row of rows.Rows) {
    row.imageUrl = await getImageHref(row.Article_Number);
    row.href = await getProductHref(row.Article_Number);
  }

  orderRows.value = rows;
});

async function getOrderRows() {
  return new Promise((resolve, reject) => {
    $.ajax({
      type: "POST",
      url: API_URL,
      data: {
        key: 'CustomerOrderRows',
        custom_fields: {
          order_no: props.orderNo
        }
      },
      success: function(data) {
        // Try to parse response to json
        try {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          resolve(parsed);
        } catch (e) {
          console.error("JSON parse error:", e);
          resolve([]);
        }
      },
      error: function(err) {
        console.error("Failed to fetch orders:", err);
        reject([]);
      }
    });
  });
}

// Get product data
async function getProductData(articleNumner) {
  const vqlQuery = {
    query: {
      products: {
        filters: {
          where: {
            model: articleNumner
          }
        },
        fields: [
        {
          "image": {
            "fields": [
              "_all"
            ]
          }
        },
        "href"
        ]
      }
    }
  };

  try {
    const response = await fetch('/surface/1/vql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(vqlQuery)
    });

    const result = await response.json();
    return result?.query?.products?.[0] || null;
  } catch (error) {
    console.error("VQL Error:", error);
  }
}

async function getImageHref(articleNumber) {
  const product = await getProductData(articleNumber);
  return product?.image?.href || null;
}

async function getProductHref(articleNumber) {
  const product = await getProductData(articleNumber);
  return product?.href || null;
}

function formatQuantity(value) {
  const numericValue = Number(value);
  if (isNaN(numericValue)) return value;
  return Math.trunc(numericValue);
}

const { formatPrice } = useCurrencyFormatter(props.currency);

function formatOrderValue(value) {
  const numericValue = Number(value);
  if (isNaN(numericValue)) return value;
  return formatPrice(numericValue);
}
</script>